import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ButtonTabsContent, ButtonTabBar, type ButtonTabItem } from '@/components/ui/button-tabs';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { toast } from 'sonner';
import {
  Loader2,
  Check,
  X,
  Mail,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle2,
  Briefcase,
  Clock,
  UserPlus,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { PhoneInput } from '@/components/ui/phone-input';
import { useCommunityMembers } from '@/hooks/use-community-members';
import { getOrderedOnboardingAnswers } from '@/lib/onboarding-answers';
import { OnboardingAnswerDisplay } from '@/components/profile/onboarding-answer-display';
import type {
  CommunityBusinessLinkRequest,
  FormAnswerValue,
  OnboardingAnswerEntry,
  OnboardingFormConfig,
} from '@/types';
import { useCommunity } from '@/hooks/use-community';
import {
  revalidateAfterCommunityBusinessListingChange,
  revalidateAfterMembershipRequestOrMembersImport,
} from '@/lib/revalidate-cache';

type ManageMembersTab = 'invite' | 'upload' | 'requests' | 'pending' | 'biz';

interface RequestUser {
  mapId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  status: string;
  metadata?: {
    onboardingAnswers?: Record<string, string | number | boolean>;
    [key: string]: unknown;
  };
}

interface Invite {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  invitedByUserId: string | null;
  role: string;
  status: string;
  createdAt: string;
}

interface ImportError {
  row: number;
  phone: string;
  error: string;
}

interface ImportResult {
  imported: number;
  skipped: number;
  errors: ImportError[];
}

export default function MemberRequestsPage() {
  const { communityId } = useParams();
  const { community: communityPkg } = useCommunity(communityId);
  const onboardingFormConfig = communityPkg?.community?.onboardingForm as
    | OnboardingFormConfig
    | null
    | undefined;
  const { mutate: globalMutate } = useSWRConfig();
  const [activeTab, setActiveTab] = useState<ManageMembersTab>('invite');
  const [requests, setRequests] = useState<RequestUser[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [businessLinkRequests, setBusinessLinkRequests] = useState<CommunityBusinessLinkRequest[]>(
    []
  );
  const [loadingBusinessLinks, setLoadingBusinessLinks] = useState(false);

  const [inviteName, setInviteName] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedByUserId, setInvitedByUserId] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const { members } = useCommunityMembers(communityId);

  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchBusinessLinkRequests = async () => {
    try {
      setLoadingBusinessLinks(true);
      const res = await axiosInstance.get(
        `/api/communities/${communityId}/business-links/requests`
      );
      setBusinessLinkRequests(res.data.requests);
    } catch (error) {
      console.error('Failed to fetch business listing requests', error);
      toast.error('Could not load business listing requests');
    } finally {
      setLoadingBusinessLinks(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/communities/${communityId}/requests`);
      setRequests(res.data.requests);
    } catch (error) {
      console.error('Failed to fetch requests', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      setInvitesLoading(true);
      const res = await axiosInstance.get(`/api/communities/${communityId}/invites`);
      setInvites(res.data.invites);
    } catch (error) {
      console.error('Failed to fetch invites', error);
    } finally {
      setInvitesLoading(false);
    }
  };

  useEffect(() => {
    if (communityId) {
      fetchRequests();
      fetchInvites();
      fetchBusinessLinkRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  const handleBusinessLinkAction = async (linkId: string, action: 'APPROVE' | 'REJECT') => {
    try {
      setActionLoading(linkId);
      await axiosInstance.patch(`/api/communities/${communityId}/business-links/requests`, {
        linkId,
        action,
      });
      toast.success(action === 'APPROVE' ? 'Listing approved' : 'Request rejected');
      setBusinessLinkRequests((prev) => prev.filter((r) => r.linkId !== linkId));
      if (communityId) {
        await revalidateAfterCommunityBusinessListingChange(globalMutate, communityId);
      }
    } catch (error) {
      console.error(error);
      toast.error('Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAction = async (userId: string, action: 'APPROVE' | 'REJECT') => {
    try {
      setActionLoading(userId);
      await axiosInstance.patch(`/api/communities/${communityId}/requests`, {
        userId,
        action,
      });
      toast.success(action === 'APPROVE' ? 'Member approved' : 'Request rejected');
      setRequests((prev) => prev.filter((r) => r.userId !== userId));
      if (communityId) {
        await revalidateAfterMembershipRequestOrMembersImport(globalMutate, communityId);
      }
    } catch (error) {
      console.error(error);
      toast.error('Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      setActionLoading(inviteId);
      await axiosInstance.delete(`/api/communities/${communityId}/invites`, {
        data: { inviteId },
      });
      toast.success('Invite revoked');
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch (error) {
      console.error(error);
      toast.error('Failed to revoke invite');
    } finally {
      setActionLoading(null);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteName || !invitePhone) return;

    setInviteLoading(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/invites/direct`, {
        name: inviteName,
        phone: invitePhone,
        email: inviteEmail || undefined,
        invitedByUserId: invitedByUserId || undefined,
      });
      toast.success('Invite sent successfully');
      setInviteName('');
      setInvitePhone('');
      setInviteEmail('');
      setInvitedByUserId('');
      fetchInvites();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleBulkImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkFile) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await axiosInstance.post(
        `/api/communities/${communityId}/members/csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setBulkResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`${res.data.imported} member(s) imported successfully`);
        if (communityId) {
          await revalidateAfterMembershipRequestOrMembersImport(globalMutate, communityId);
        }
      } else {
        toast.info('No new members were imported');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const resetBulkImport = () => {
    setBulkFile(null);
    setBulkResult(null);
  };

  const tabItems = useMemo<ButtonTabItem<ManageMembersTab>[]>(
    () => [
      { value: 'invite', label: 'Invite', icon: Mail },
      { value: 'upload', label: 'Upload', icon: Upload },
      {
        value: 'requests',
        label: 'Requests',
        icon: UserPlus,
        badge: requests.length || null,
        badgeTone: 'amber',
      },
      {
        value: 'pending',
        label: 'Pending',
        icon: Clock,
        badge: invites.length || null,
        badgeTone: 'amber',
      },
      {
        value: 'biz',
        label: 'Biz',
        icon: Briefcase,
        badge: businessLinkRequests.length || null,
        badgeTone: 'amber',
      },
    ],
    [requests.length, invites.length, businessLinkRequests.length]
  );

  const handleTabChange = (value: ManageMembersTab) => {
    setActiveTab(value);
    if (value === 'pending' && invites.length === 0 && !invitesLoading) {
      fetchInvites();
    }
    if (value === 'biz') {
      fetchBusinessLinkRequests();
    }
  };

  return (
    <div className="space-y-6 w-full max-w-4xl mx-auto px-0 py-0 sm:container sm:py-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Manage Members</h2>
        <p className="text-muted-foreground">Review join requests and invite new members.</p>
      </div>

      <ButtonTabBar
        items={tabItems}
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-0"
      >
        <ButtonTabsContent value="invite">
          <Card>
            <CardHeader>
              <CardTitle>Invite Members</CardTitle>
              <CardDescription>Send an invite to join this community.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <PhoneInput
                    id="phone"
                    placeholder="Enter phone number"
                    value={invitePhone}
                    onChange={(val) => setInvitePhone(val || '')}
                    defaultCountry="IN"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email (optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Invited by (optional)</Label>
                  <Combobox
                    options={members.map((m) => ({
                      value: m.id,
                      label: m.businessName ? `${m.name} (${m.businessName})` : m.name,
                    }))}
                    value={invitedByUserId || undefined}
                    onSelect={(val) => setInvitedByUserId(val || '')}
                    placeholder="Search & select a member..."
                    searchPlaceholder="Search by name..."
                  />
                </div>
                <Button type="submit" disabled={inviteLoading} className="gap-2">
                  {inviteLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Send Invite
                </Button>
              </form>
            </CardContent>
          </Card>
        </ButtonTabsContent>

        <ButtonTabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Import Members</CardTitle>
              <CardDescription>Upload a CSV file to add multiple members at once.</CardDescription>
            </CardHeader>
            <CardContent>
              {!bulkResult ? (
                <form onSubmit={handleBulkImport} className="space-y-4">
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-muted-foreground">
                    <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                      <FileText className="h-4 w-4" /> Required CSV columns:
                    </p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>
                        <span className="font-mono text-xs bg-gray-200 px-1 rounded">name</span> —
                        Full name (first middle last)
                      </li>
                      <li>
                        <span className="font-mono text-xs bg-gray-200 px-1 rounded">email</span> —
                        Personal email (optional)
                      </li>
                      <li>
                        <span className="font-mono text-xs bg-gray-200 px-1 rounded">phone</span> —
                        Phone with country code <span className="text-red-500">*required</span>{' '}
                        (e.g. +919876543210)
                      </li>
                      <li>
                        <span className="font-mono text-xs bg-gray-200 px-1 rounded">
                          business_name
                        </span>{' '}
                        — Business name (optional)
                      </li>
                      <li>
                        <span className="font-mono text-xs bg-gray-200 px-1 rounded">
                          business_email
                        </span>{' '}
                        — Business email (optional)
                      </li>
                    </ul>
                  </div>

                  <div
                    className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 p-8 cursor-pointer hover:border-primary/60 hover:bg-primary/5 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    {bulkFile ? (
                      <p className="text-sm font-medium text-primary">{bulkFile.name}</p>
                    ) : (
                      <>
                        <p className="text-sm font-medium">Click to select a CSV file</p>
                        <p className="text-xs text-muted-foreground">.csv files only</p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetBulkImport}
                      disabled={!bulkFile || bulkLoading}
                    >
                      Clear
                    </Button>
                    <Button type="submit" disabled={!bulkFile || bulkLoading} className="gap-2">
                      {bulkLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                      Import Members
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border bg-green-50 border-green-200 p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{bulkResult.imported}</p>
                      <p className="text-xs text-green-600">Imported</p>
                    </div>
                    <div className="rounded-lg border bg-amber-50 border-amber-200 p-3 text-center">
                      <p className="text-2xl font-bold text-amber-700">{bulkResult.skipped}</p>
                      <p className="text-xs text-amber-600">Skipped</p>
                    </div>
                  </div>

                  {bulkResult.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold flex items-center gap-1 text-red-600">
                        <AlertCircle className="h-4 w-4" /> Errors ({bulkResult.errors.length})
                      </p>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {bulkResult.errors.map((err, idx) => (
                          <div
                            key={idx}
                            className="text-xs bg-red-50 border border-red-200 rounded p-2"
                          >
                            <span className="font-semibold">Row {err.row}</span>
                            {err.phone && (
                              <span className="text-muted-foreground"> ({err.phone})</span>
                            )}
                            : {err.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {bulkResult.imported > 0 && bulkResult.errors.length === 0 && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      All rows processed successfully!
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={resetBulkImport}>
                      Import Another
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </ButtonTabsContent>

        <ButtonTabsContent value="requests">
          <Card>
            <CardHeader>
              <CardTitle>Join Requests</CardTitle>
              <CardDescription>People waiting to join your community.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No pending requests found.
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((req) => (
                    <div
                      key={req.mapId}
                      className="flex items-start justify-between p-4 bg-accent/5 rounded-lg border border-border"
                    >
                      <div className="flex gap-4">
                        <Link
                          to={`/${communityId}/u/${req.userId}`}
                          className="shrink-0 hover:opacity-80 transition-opacity"
                        >
                          <Avatar>
                            <AvatarImage
                              src={`https://api.dicebear.com/7.x/initials/svg?seed=${req.firstName} ${req.lastName}`}
                            />
                            <AvatarFallback>
                              {req.firstName?.[0]}
                              {req.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                        </Link>
                        <div>
                          <Link
                            to={`/${communityId}/u/${req.userId}`}
                            className="group hover:opacity-80 transition-opacity"
                          >
                            <h4 className="font-semibold group-hover:underline">
                              {req.firstName} {req.lastName}
                            </h4>
                          </Link>
                          <p className="text-sm text-muted-foreground">{req.email}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Requested: {new Date(req.createdAt).toLocaleDateString()}
                          </p>

                          {req.metadata?.onboardingAnswers && (
                            <div className="mt-3 text-sm bg-background p-3 rounded border">
                              <p className="font-semibold text-xs text-muted-foreground mb-1">
                                Additional details:
                              </p>
                              {getOrderedOnboardingAnswers(
                                onboardingFormConfig,
                                req.metadata.onboardingAnswers as Record<
                                  string,
                                  FormAnswerValue | OnboardingAnswerEntry
                                >
                              ).map(({ field, question, answer }) => (
                                <div key={field.id} className="mb-1">
                                  <span className="text-muted-foreground text-xs block">
                                    {question}
                                  </span>
                                  <OnboardingAnswerDisplay
                                    field={field}
                                    answer={answer}
                                    emptyLabel="—"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleAction(req.userId, 'REJECT')}
                          disabled={!!actionLoading}
                        >
                          {actionLoading === req.userId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAction(req.userId, 'APPROVE')}
                          disabled={!!actionLoading}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {actionLoading === req.userId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </ButtonTabsContent>

        <ButtonTabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Sent Invites</CardTitle>
              <CardDescription>
                Invites you have sent that are still pending. These users will likely have received
                an email notification.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invitesLoading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : invites.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No pending invites. Use the Invite tab to add members.
                </div>
              ) : (
                <div className="space-y-2">
                  {invites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between p-4 bg-accent/5 rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                          <Mail className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {invite.name ||
                              invite.email ||
                              formatDisplayPhone(invite.phone) ||
                              'Unknown Contact'}
                          </p>
                          {(invite.name || invite.email || invite.phone) && (
                            <p className="text-xs text-muted-foreground">
                              {invite.email && invite.email}
                              {invite.email && invite.phone && ' · '}
                              {invite.phone && formatDisplayPhone(invite.phone)}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Sent: {new Date(invite.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleRevokeInvite(invite.id)}
                        disabled={actionLoading === invite.id}
                      >
                        {actionLoading === invite.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Revoke'
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </ButtonTabsContent>

        <ButtonTabsContent value="biz">
          <Card>
            <CardHeader>
              <CardTitle>Business listing requests</CardTitle>
              <CardDescription>
                Members asked to list a business in the directory (when curated listings and
                approval are enabled).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingBusinessLinks ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : businessLinkRequests.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No pending business listing requests.
                </div>
              ) : (
                <div className="space-y-4">
                  {businessLinkRequests.map((req) => (
                    <div
                      key={req.linkId}
                      className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-4 bg-accent/5 rounded-lg border border-border"
                    >
                      <div className="space-y-1 min-w-0">
                        <p className="font-semibold text-base truncate">{req.businessName}</p>
                        <p className="text-sm text-muted-foreground">
                          {req.firstName} {req.lastName}
                          <span className="mx-2 opacity-40">·</span>
                          {req.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Requested {new Date(req.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleBusinessLinkAction(req.linkId, 'REJECT')}
                          disabled={!!actionLoading}
                        >
                          {actionLoading === req.linkId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleBusinessLinkAction(req.linkId, 'APPROVE')}
                          disabled={!!actionLoading}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {actionLoading === req.linkId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </ButtonTabsContent>
      </ButtonTabBar>
    </div>
  );
}
